/* eslint-disable react-hooks/exhaustive-deps */
import 'firebase/auth';
import 'firebase/firestore';
import PropTypes from 'prop-types';
import firebase from 'firebase/app';
import { store } from 'src/redux/store';
import useAuth from 'src/hooks/useAuth';
import React, { useEffect, useState } from 'react';
import { firebaseConfig } from 'src/config';
import { ReactReduxFirebaseProvider } from 'react-redux-firebase';
import { useDispatch } from 'react-redux'
import { setMyDistributors, setUserDetail } from 'src/redux/slices/authFirebase'
import routes, { renderRoutes } from 'src/routes'
import _ from 'lodash'
import * as rdd from 'react-device-detect'
import { getUserByToken } from 'src/api/customer';
import { listDistributorV2 } from 'src/api/distributor';
import { createCatalogueCart, getCatalogueCart } from 'src/api/catalogue';
import { updateCart } from 'src/redux/slices/instock';
import LogoutView from 'src/views/auth/LogoutView';
import AuthResolvingScreen from 'src/components/AuthResolvingScreen';

// ----------------------------------------------------------------------

if (!firebase.apps.length) {
  firebase.initializeApp(firebaseConfig);
  //firebase.firestore();
}

// No userProfile config on purpose. The Minimals template shipped
// { userProfile: 'users', useFirestoreForProfile: false }, which tells
// react-redux-firebase to keep profiles in Realtime Database — but neither RTDB
// nor Firestore is initialised here, so RRF threw "Real Time Database or
// Firestore must be included to enable user profile" and firebase.login()
// REJECTED even when Firebase Auth had succeeded. Nothing consumed that profile
// (useAuth destructures `profile` but never returns it), so it just breaks login.
const rrfProps = {
  firebase,
  config: {},
  dispatch: store.dispatch,
  //createFirestoreInstance
};


// ----------------------------------------------------------------------

FirebaseProvider.propTypes = {
  children: PropTypes.node
};

function FirebaseProvider({ children }) {
  const { user, isAuthenticated, isLoading, logout } = useAuth()
  const dispatch = useDispatch()

  const [checkMobile, setCheckMobile] = useState(false)

  // Authoritative auth signal, straight from Firebase.
  //   undefined = Firebase hasn't told us yet   → hold the loading screen
  //   null      = definitely signed out         → login tree
  //   object    = signed in                     → wait for user.status
  // Needed because react-redux-firebase flips isLoaded(auth) to TRUE with an
  // EMPTY auth object before the stored session is restored. In that window
  // isLoading=false and isAuthenticated=false, which is indistinguishable from
  // "signed out" — and that is exactly when the login page flashed.
  const [firebaseUser, setFirebaseUser] = useState(undefined)

  useEffect(() => {
    const unsubscribe = firebase.auth().onAuthStateChanged((u) => setFirebaseUser(u || null))
    return () => unsubscribe()
  }, [])


  useEffect(() => {
    const getInitialize = async () => {
      try {
        firebase.auth().onAuthStateChanged(async (user) => {
          if (user && isAuthenticated) {  
            let token = await user.getIdToken()
            // Never log the ID token (or the user object, which carries it):
            // it is a bearer credential valid ~1h, and console output ends up in
            // screenshots and support threads.
            const userDB = await getUserByToken(token);

            // Dispatch as soon as the user is known. This sets user.status, which
            // is what releases the loading screen and picks the route tree — it
            // must NOT wait on the distributor call below, otherwise the UI is
            // held hostage by a request it doesn't need to render.
            dispatch(setUserDetail(userDB.data));

            const distributors = await listDistributorV2(token, {
              page: 0,
              pageSize: 10,
              userID: userDB?.data?._id,
              retailerID: userDB?.data?.retailerID?._id
            });

            dispatch(
              setMyDistributors({
                distributors: distributors?.filter(
                  (val) => val.applyStatus === 'approved'
                ) || [],
              })
            );
            
            const catalogueCart = await getCatalogueCart(token, { 
              retailerID: userDB?.data?.retailerID?._id
            });
            if(catalogueCart.data === null){
              const createdCart = await createCatalogueCart(token, {
                retailerID: userDB?.data?.retailerID?._id,
              });
              dispatch(updateCart({ cart: createdCart.data.cart }));
            } else {
              dispatch(updateCart({ cart: catalogueCart.data.cart }));
            }

            setCheckMobile(rdd.isMobileOnly);
          }
          else if(_.isNull(user) && !isAuthenticated){
            await logout();
          }
        })
      } catch (error) {
        console.error(error)
      }
    }

    getInitialize();
  }, [isAuthenticated]);

  // /logout is handled here, ahead of the role-based route trees, so it works in
  // every auth state (active / pending / signed-out) without having to add the
  // route to all fifteen route arrays. It has to live inside
  // ReactReduxFirebaseProvider because useAuth().logout() needs the firebase
  // instance from context.
  const isLogoutRoute = window.location.pathname.replace(/\/+$/, '') === '/logout';

  // Firebase says this user is signed in, but their profile/status hasn't come
  // back from the API yet. Without this, "unknown" fell through to the same
  // branch as "signed out" and rendered the LOGIN tree — which is why people saw
  // login → loading → dashboard → LOGIN → dashboard. Hold a loading screen until
  // we actually know, and offer a manual reload if it drags on.
  // Two distinct "we don't know yet" windows, and BOTH must hold the loading
  // screen or the login page flashes:
  //   1. isLoading — Firebase hasn't restored the session from storage yet, so
  //      isAuthenticated is still false. On a cold boot (and every hard reload)
  //      this comes FIRST, which is why gating only on (2) still flashed.
  //   2. authenticated, but the profile/status fetch hasn't returned.
  const isResolvingUser =
    firebaseUser === undefined || isLoading || (firebaseUser && !user.status);



  return (
    <ReactReduxFirebaseProvider {...rrfProps}>
      {children}
      {isLogoutRoute ? (
        <LogoutView />
      ) : isResolvingUser ? (
        <AuthResolvingScreen />
      ) : user.status === 'active' ? (
        <>

          {
            checkMobile ? (
              user?.hasSubmitB2BForm === true ? (
                <>
                  {user?.b2bFormStatus === 'approved' ? (
                    user?.isStaff === true ? (
                      renderRoutes(routes.routesCustomerMobile)
                    )
                    :
                    (
                      renderRoutes(routes.routesCustomerMobile)
                    )
                    
                  ) 
                  : 
                  (
                    renderRoutes(routes.routesCustomerMobAppReview)
                  )
                }
                </>
              )
              :
              (
                renderRoutes(routes.routesCustomerMobileRegister)
              )
            )
            :
            (
              user?.hasSubmitB2BForm === true ? (
                <>
                  {user?.b2bFormStatus === 'approved' ? (
                    user?.isStaff === true ? (
                      renderRoutes(routes.routesCustomer)
                    )
                    :
                    (
                      renderRoutes(routes.routesCustomer)
                    )
                    
                  ) 
                  : 
                  (
                    renderRoutes(routes.routesCustomerAppReview)
                  )
                }
                </>
              )
              :
              (
                renderRoutes(routes.routesCustomerRegister)
              )
            )
          }



          {/**{!_.isEmpty(user?.signUpToDistributor) ? (
            <>
              {user?.hasSubmitB2BForm === true ? (
                <>
                  {user?.b2bFormStatus === 'approved'
                    ? renderRoutes(routes.routesCustomer)
                    : renderRoutes(routes.routesCustomerAppReview)}
                </>
              ) : (
                renderRoutes(routes.routesCustomerRegister)
              )}
            </>
          ) : (
            renderRoutes(routes.routesCustomer)
          )}*/}


        </>
      ) : user.status === 'pending' ? (
        renderRoutes(routes.routesCustomerUnverified)
      ) : (
        renderRoutes(routes.routes)
      )}

      {/* {
        _.isEmpty(user?.signUpToDistributor) ? (
          // old data
          renderRoutes(routes.routesCustomer)
        ) 
        :
        (
          user?.hasSubmitB2BForm === true ? (
            user?.b2bFormStatus === 'pending' || user?.b2bFormStatus === 'rejected' ? (
              renderRoutes(routes.routesCustomerAppReview)
            )
            :
            (
              renderRoutes(routes.routesCustomer)
            )
          )
          :
          (
            renderRoutes(routes.routesCustomerRegister)
          )
        )
      } */}
    </ReactReduxFirebaseProvider>
  );
}

export default FirebaseProvider;
